import React from 'react';
import { dayjs } from 'shared/utils/date';
import { FormattedMessage } from 'react-intl';
import { Select, InputNumber, Checkbox, DatePicker } from 'antd';
import { PERIOD_TYPE, PERIOD, COMPARE_PERIOD, calculateMainDateRange, calculateDateRanges } from 'shared/constants/date';
import messages from './messages';

import PreviewDates from './date-preview';

const { LAST, THIS, FIXED, FIXED_WEEKS } = PERIOD_TYPE;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Period = ({
  dateRange: {
    periodType,
    period,
    lastNumberInput,
    specificDates,
  },
  handleNumberChange,
  handlePeriodChange,
  handleSpecificDatesChange,
}) => {
  switch (periodType) {
    case LAST: {
      return (
        <>
          <InputNumber
            className="date-range-picker__input"
            min={1}
            value={lastNumberInput}
            onChange={handleNumberChange}
          />
          <Select className="date-range-picker__select" value={period || PERIOD.DAY} onChange={handlePeriodChange}>
            <Option value={PERIOD.DAY}><FormattedMessage {...messages.days} /></Option>
            <Option value={PERIOD.WEEK}><FormattedMessage {...messages.weeks} /></Option>
            <Option value={PERIOD.MONTH}><FormattedMessage {...messages.months} /></Option>
            <Option value={PERIOD.QUARTER}><FormattedMessage {...messages.quarters} /></Option>
            <Option value={PERIOD.YEAR}><FormattedMessage {...messages.years} /></Option>
          </Select>
        </>
      );
    }
    case THIS: {
      return (
        <Select className="date-range-picker__select" value={period || PERIOD.MONTH} onChange={handlePeriodChange}>
          <Option value={PERIOD.DAY}><FormattedMessage {...messages.day} /></Option>
          <Option value={PERIOD.WEEK}><FormattedMessage {...messages.week} /></Option>
          <Option value={PERIOD.MONTH}><FormattedMessage {...messages.month} /></Option>
          <Option value={PERIOD.QUARTER}><FormattedMessage {...messages.quarter} /></Option>
          <Option value={PERIOD.YEAR}><FormattedMessage {...messages.year} /></Option>
        </Select>
      );
    }
    case FIXED: {
      return (
        <RangePicker
          // format={dayjs().creationData().locale._longDateFormat.L} // eslint-disable-line
          value={[dayjs(specificDates?.startDate), dayjs(specificDates?.endDate)]}
          onChange={handleSpecificDatesChange}
          allowClear={false}
        />
      );
    }
    case FIXED_WEEKS: {
      return (
        <RangePicker
          picker="week"
          value={[dayjs(specificDates?.startDate), dayjs(specificDates?.endDate)]}
          onChange={handleSpecificDatesChange}
          allowClear={false}
          format="w gggg"
        />
      );
    }
    default:
      return '';
  }
};

const CompareToPrevious = ({
  dateRange,
  toggleCompareToPrevious,
  handleCompareValueChange,
  handleComparePeriodChange,
  handleCompareSpecificDatesChange,
}) => {
  const {
    compareToPrevious,
    compareToPreviousValue,
    comparePeriod,
    compareSpecificDates,
  } = dateRange;

  const compareSpecificPickers = [];

  if (compareToPrevious && comparePeriod === COMPARE_PERIOD.FIXED) {
    for (let i = 0; i < compareToPreviousValue; i++) {
      const { startDate, endDate } = compareSpecificDates?.length > i ? compareSpecificDates[i] : {};
      compareSpecificPickers.push(
        <div key={i} className="date-range-picker__compare-specific">
          <RangePicker
            value={[dayjs(startDate), dayjs(endDate)]}
            onChange={(value) => handleCompareSpecificDatesChange(value, i)}
            allowClear={false}
            style={{ marginTop: '5px' }}
          />
        </div>,
      );
    }
  }

  return (
    <>
      <div className="date-range-picker__compare">
        <Checkbox id="compare" checked={compareToPrevious} onChange={toggleCompareToPrevious}>
          <FormattedMessage {...messages.compareToPrevious} />
        </Checkbox>
        <InputNumber
          className="date-range-picker__input"
          disabled={!compareToPrevious}
          min={1}
          max={3}
          value={compareToPreviousValue}
          onChange={handleCompareValueChange}
        />
        <Select
          className="date-range-picker__select"
          disabled={!compareToPrevious}
          value={comparePeriod || 'period'}
          onChange={handleComparePeriodChange}
        >
          <Option value="period"><FormattedMessage {...messages.previous} /></Option>
          <Option value="year"><FormattedMessage {...messages.previousYears} /></Option>
          <Option value={FIXED}><FormattedMessage {...messages.compareFixed} /></Option>
        </Select>
      </div>
      {compareSpecificPickers.length ? compareSpecificPickers : <PreviewDates dateRange={dateRange} compareOnly />}
    </>
  );
};

const DateSelection = ({ value, onChange, allowCompare }) => {
  const updateDateRange = (update) => {
    onChange({ ...value, ...update });
  };

  const handlePeriodTypeChange = (periodType) => {
    if (periodType === PERIOD_TYPE.FIXED) {
      const specificDates = calculateMainDateRange(dayjs, value);
      updateDateRange({ periodType, period: PERIOD.DAY, specificDates });
    } else if (periodType === PERIOD_TYPE.FIXED_WEEKS) {
      const specificDates = calculateMainDateRange(dayjs, value);
      updateDateRange({ periodType, period: PERIOD.WEEK, specificDates });
    } else if (periodType === PERIOD_TYPE.THIS) {
      updateDateRange({ periodType, lastNumberInput: 1 });
    } else {
      updateDateRange({ periodType });
    }
  };

  const handlePeriodChange = period => updateDateRange({ period });
  const handleNumberChange = lastNumberInput => updateDateRange({ lastNumberInput });
  const handleComparePeriodChange = comparePeriod => {
    if (comparePeriod === COMPARE_PERIOD.FIXED) {
      const [, ...compareSpecificDates] = calculateDateRanges(dayjs, value);
      updateDateRange({ comparePeriod, compareSpecificDates });
    } else {
      updateDateRange({ comparePeriod });
    }
  };
  const toggleCompareToPrevious = () => updateDateRange({ compareToPrevious: !value.compareToPrevious });
  const handleCompareValueChange = compareToPreviousValue => {
    if (value.comparePeriod === COMPARE_PERIOD.FIXED) {
      const [, ...compareSpecificDates] = calculateDateRanges(dayjs, { ...value, compareToPreviousValue });
      updateDateRange({ compareToPreviousValue, compareSpecificDates });
    } else {
      updateDateRange({ compareToPreviousValue });
    }
  };

  const handleSpecificDatesChange = ([startDate, endDate]) => {
    updateDateRange({
      specificDates: { startDate: startDate.toDate(), endDate: endDate.endOf('day').toDate() },
    });
  };

  const handleCompareSpecificDatesChange = ([startDate, endDate], index) => {
    const compareSpecificDates = [...value.compareSpecificDates];

    compareSpecificDates[index] = { startDate: startDate.toDate(), endDate: endDate.endOf('day').toDate() };

    updateDateRange({
      compareSpecificDates,
    });
  };

  return (
    <>
      <div className="date-range-picker__periods">
        <Select
          className="date-range-picker__select"
          onChange={handlePeriodTypeChange}
          value={value.periodType}
        >
          <Option value={LAST}><FormattedMessage {...messages.last} /></Option>
          <Option value={THIS}><FormattedMessage {...messages.this} /></Option>
          <Option value={FIXED}><FormattedMessage {...messages.fixed} /></Option>
          <Option value={FIXED_WEEKS}><FormattedMessage {...messages.fixedWeeks} /></Option>
        </Select>

        <Period
          dateRange={value}
          handleSpecificDatesChange={handleSpecificDatesChange}
          handlePeriodChange={handlePeriodChange}
          handleNumberChange={handleNumberChange}
        />
      </div>
      {value.periodType !== PERIOD_TYPE.FIXED && <PreviewDates dateRange={value} mainOnly />}

      {allowCompare && (
      <CompareToPrevious
        dateRange={value}
        toggleCompareToPrevious={toggleCompareToPrevious}
        handleCompareValueChange={handleCompareValueChange}
        handleComparePeriodChange={handleComparePeriodChange}
        handleCompareSpecificDatesChange={handleCompareSpecificDatesChange}
      />
      )}
    </>
  );
};

export default DateSelection;
